import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, getProjects, getAccessibleProjects, createProject, updateProject, archiveProject, unarchiveProject } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { CreateProjectModal } from '../components/CreateProjectModal';
import { EditProjectModal } from '../components/EditProjectModal';
import { ProjectSettings } from '../components/project/ProjectSettings';
import { PermissionsProvider } from '../contexts/PermissionsContext';
import {
  FolderPlus,
  Plus,
  FolderKanban,
  Calendar,
  Users,
  MoreVertical,
  Edit2,
  Settings,
  Archive,
  ArchiveRestore,
  Trash2
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';

interface UserData {
  _id: string;
  name: string;
  email: string;
}

interface JiraConfig {
  projectKey: string;
  jiraUrl: string;
  username: string;
  apiToken: string;
}

interface Project {
  _id: string;
  name: string;
  description?: string;
  hoursTrackingEnabled?: boolean;
  jiraEnabled?: boolean;
  jiraConfig?: JiraConfig;
  archived?: boolean;
  archivedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const Projects: React.FC = () => {
  const { user } = useUser();
  const [projects, setProjects] = useState<Project[]>([]);
  const [archivedProjects, setArchivedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeMenuProject, setActiveMenuProject] = useState<string | null>(null);
  const [projectSettingsProject, setProjectSettingsProject] = useState<Project | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load projects from API using team-based access
        const projectsResponse = await getAccessibleProjects(false);
        setProjects(projectsResponse.data.data.projects);
        
        // Load archived projects
        const archivedResponse = await getAccessibleProjects(true);
        setArchivedProjects(archivedResponse.data.data.projects);
      } catch (error) {
        console.error('Error loading projects:', error);
        // Don't redirect here, let the ProtectedRoute handle it
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if user is authenticated
    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Clear projects when user is logged out
  useEffect(() => {
    if (!user) {
      setProjects([]);
      setArchivedProjects([]);
    }
  }, [user]);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.project-menu-button') && !target.closest('.project-menu-dropdown')) {
        setActiveMenuProject(null);
      }
    };

    if (activeMenuProject) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeMenuProject]);

  const handleCreateProject = async (projectData: {
    name: string;
    description?: string;
    jiraEnabled?: boolean;
    jiraConfig?: JiraConfig;
  }) => {
    try {
      const response = await createProject(projectData);
      const newProject = response.data.data.project;
      
      setProjects(prev => [newProject, ...prev]);
    } catch (error) {
      console.error('Error creating project:', error);
      // TODO: Add proper error handling/notification
    }
  };

  const handleEditProject = (project: Project) => {
    console.log('Edit project clicked:', project.name);
    setActiveMenuProject(null);
    setEditingProject(project);
  };

  const handleUpdateProject = async (projectId: string, projectData: {
    name: string;
    description?: string;
    jiraEnabled?: boolean;
    jiraConfig?: JiraConfig;
  }) => {
    try {
      const response = await updateProject(projectId, projectData);
      const updatedProject = response.data.data.project;
      
      // Update projects list with the response from API
      setProjects(prev => prev.map(p => 
        p._id === projectId ? updatedProject : p
      ));
      
      console.log(`Projeto "${projectData.name}" atualizado com sucesso!`);
    } catch (error) {
      console.error('Error updating project:', error);
      // TODO: Add proper error handling/notification
      throw error; // Re-throw to handle in modal
    }
  };

  const handleConfigureHoursBank = (project: Project) => {
    console.log('Configure project settings clicked:', project.name);
    setActiveMenuProject(null);
    setProjectSettingsProject(project);
  };

  const handleArchiveProject = async (project: Project) => {
    console.log('Archive project clicked:', project.name);
    setActiveMenuProject(null);
    if (window.confirm(`Tem certeza que deseja arquivar o projeto "${project.name}"?`)) {
      try {
        await archiveProject(project._id);
        
        // Remove archived project from the current list and add to archived
        setProjects(prev => prev.filter(p => p._id !== project._id));
        setArchivedProjects(prev => [...prev, { ...project, archived: true, archivedAt: new Date().toISOString() }]);
        
        console.log(`Projeto "${project.name}" arquivado com sucesso!`);
      } catch (error) {
        console.error('Error archiving project:', error);
        alert(`Erro ao arquivar projeto: ${project.name}`);
      }
    }
  };

  const handleUnarchiveProject = async (project: Project) => {
    console.log('Unarchive project clicked:', project.name);
    setActiveMenuProject(null);
    if (window.confirm(`Tem certeza que deseja restaurar o projeto "${project.name}"?`)) {
      try {
        await unarchiveProject(project._id);
        
        // Remove from archived list and add to active projects
        setArchivedProjects(prev => prev.filter(p => p._id !== project._id));
        setProjects(prev => [...prev, { ...project, archived: false, archivedAt: undefined }]);
        
        console.log(`Projeto "${project.name}" restaurado com sucesso!`);
      } catch (error) {
        console.error('Error unarchiving project:', error);
        alert(`Erro ao restaurar projeto: ${project.name}`);
      }
    }
  };

  const handleProjectCardClick = (project: Project, event: React.MouseEvent) => {
    // Don't navigate if clicking on the menu button or dropdown
    const target = event.target as HTMLElement;
    if (target.closest('.project-menu-button') || target.closest('.project-menu-dropdown')) {
      return;
    }
    navigate(`/projects/${project._id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary">
        <LoadingSpinner message="Carregando projetos" />
      </div>
    );
  }

  return (
    <Layout currentPage="projects">
      <div className="h-full overflow-y-auto custom-scrollbar">
        <div className="p-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground-primary mb-2 neon-blue">
              Projects
            </h1>
          </div>

          {/* View Toggle */}
          <div className="mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant={!showArchived ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchived(false)}
              >
                <FolderKanban className="w-4 h-4 mr-2" />
                Projetos Ativos ({projects.length})
              </Button>
              <Button
                variant={showArchived ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchived(true)}
              >
                <Archive className="w-4 h-4 mr-2" />
                Projetos Arquivados ({archivedProjects.length})
              </Button>
            </div>
          </div>

          {/* Projects List or Empty State */}
          {!showArchived && projects.length === 0 ? (
            <Card variant="glass" className="w-full text-center p-12 card-hover">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center">
                  <FolderPlus className="w-10 h-10 text-blue-400" />
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-foreground-primary mb-2">
                    Nenhum projeto ainda
                  </h2>
                  <p className="text-foreground-tertiary">
                    Comece organizando seu trabalho criando seu primeiro projeto
                  </p>
                </div>

                <Button 
                  variant="gradient" 
                  size="lg" 
                  className="text-base font-semibold"
                  onClick={() => setIsModalOpen(true)}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Criar primeiro projeto
                </Button>
              </CardContent>
            </Card>
          ) : showArchived && archivedProjects.length === 0 ? (
            <Card variant="glass" className="w-full text-center p-12 card-hover">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full flex items-center justify-center">
                  <Archive className="w-10 h-10 text-orange-400" />
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-foreground-primary mb-2">
                    Nenhum projeto arquivado
                  </h2>
                  <p className="text-foreground-tertiary">
                    Projetos arquivados aparecerão aqui
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Projects Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-heading text-foreground-primary">
                    {showArchived ? `Projetos Arquivados (${archivedProjects.length})` : `Meus Projetos (${projects.length})`}
                  </h2>
                  <p className="text-foreground-tertiary">
                    {showArchived ? 'Projetos arquivados - podem ser restaurados ou deletados permanentemente' : 'Gerencie e acompanhe seus projetos'}
                  </p>
                </div>
                {!showArchived && (
                  <Button 
                    variant="gradient" 
                    onClick={() => setIsModalOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Projeto
                  </Button>
                )}
              </div>

              {/* Projects Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(showArchived ? archivedProjects : projects).map((project) => (
                  <Card 
                    key={project._id} 
                    variant="glass" 
                    className="card-hover cursor-pointer relative group"
                    onClick={(e) => handleProjectCardClick(project, e)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FolderKanban className="w-4 h-4 text-blue-400" />
                            </div>
                            <CardTitle className="text-lg font-heading text-foreground-primary line-clamp-2">
                              {project.name}
                            </CardTitle>
                          </div>
                          {project.description && (
                            <p className="text-sm text-foreground-tertiary mt-2 line-clamp-2">
                              {project.description}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Actions Menu */}
                          <div className="relative project-menu-button">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveMenuProject(activeMenuProject === project._id ? null : project._id);
                              }}
                              className="text-foreground-tertiary hover:text-foreground-primary"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>

                            {activeMenuProject === project._id && (
                              <div className="absolute right-0 top-8 z-50 w-48 bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-lg shadow-xl py-2 project-menu-dropdown">
                                {!showArchived ? (
                                  <>
                                    <button
                                      onClick={() => {
                                        handleEditProject(project);
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm text-foreground-secondary hover:text-foreground-primary hover:bg-white/5 rounded flex items-center gap-2"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                      Editar
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleConfigureHoursBank(project);
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm text-foreground-secondary hover:text-foreground-primary hover:bg-white/5 rounded flex items-center gap-2"
                                    >
                                      <Settings className="w-3 h-3" />
                                      Configuração
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleArchiveProject(project);
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 rounded flex items-center gap-2"
                                    >
                                      <Archive className="w-3 h-3" />
                                      Arquivar
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => {
                                        handleUnarchiveProject(project);
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded flex items-center gap-2"
                                    >
                                      <ArchiveRestore className="w-3 h-3" />
                                      Restaurar
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Tem certeza que deseja excluir PERMANENTEMENTE o projeto "${project.name}"? Esta ação não pode ser desfeita.`)) {
                                          // TODO: Implement permanent delete
                                          alert('Funcionalidade de exclusão permanente será implementada em breve');
                                        }
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded flex items-center gap-2"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                      Excluir Permanentemente
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <div className="flex justify-between text-xs text-foreground-tertiary pt-3 border-t border-white/10">
                        {showArchived && project.archivedAt ? (
                          <>
                            <span>Arquivado em {formatDate(project.archivedAt)}</span>
                            <span className="text-orange-400">Arquivado</span>
                          </>
                        ) : (
                          <>
                            <span>Criado em {formatDate(project.createdAt)}</span>
                            <span>Atualizado em {formatDate(project.updatedAt)}</span>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Create Project Modal */}
          <CreateProjectModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onCreateProject={handleCreateProject}
          />

          {/* Edit Project Modal */}
          <EditProjectModal
            isOpen={!!editingProject}
            onClose={() => setEditingProject(null)}
            onEditProject={handleUpdateProject}
            project={editingProject}
          />

          {/* Project Settings Modal */}
          {projectSettingsProject && (
            <PermissionsProvider projectId={projectSettingsProject._id}>
              <ProjectSettings
                projectId={projectSettingsProject._id}
                projectName={projectSettingsProject.name}
                isOpen={true}
                onClose={() => setProjectSettingsProject(null)}
              />
            </PermissionsProvider>
          )}

        </div>
      </div>

    </Layout>
  );
};

export default Projects;
